/**
 * Size Guide Modal interactions — GCHM-38
 *
 * Custom element <hm-size-guide> handling:
 *   - Regular / Maternity tab switching
 *   - Size-range selection (XXS-S, M-XL, ...)
 *   - cm / inches unit toggle
 *
 * Open / close / Escape / click-outside / focus-trap / scroll-lock are all
 * provided by the theme's <dialog-component> wrapper, so this file only owns
 * the in-modal content state.
 */
class HmSizeGuide extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#syncUnit(this.dataset.activeUnit || 'cm');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    const tab = event.target.closest('[data-tab]');
    if (tab) return this.#selectTab(tab.dataset.tab);

    const range = event.target.closest('[data-range]');
    if (range && range.matches('.hm-size-guide__range')) {
      return this.#selectRange(range);
    }

    const unit = event.target.closest('[data-unit]');
    if (unit && unit.matches('.hm-size-guide__unit')) {
      return this.#syncUnit(unit.dataset.unit);
    }
  };

  /** Show the chosen tab panel, hide the rest. */
  #selectTab(name) {
    this.querySelectorAll('[data-tab]').forEach((btn) => {
      btn.setAttribute('aria-selected', String(btn.dataset.tab === name));
    });
    this.querySelectorAll('[data-tab-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.tabPanel !== name;
    });
    // Show the matching tab's "How to measure" (each tab has its own); hide the rest.
    this.querySelectorAll('[data-htm-tab]').forEach((htm) => {
      htm.hidden = htm.dataset.htmTab !== name;
    });
  }

  /** Within the active panel, show the chosen range's tables. */
  #selectRange(button) {
    const panel = button.closest('[data-tab-panel]');
    if (!panel) return;
    const name = button.dataset.range;

    panel.querySelectorAll('.hm-size-guide__range').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.range === name));
    });
    panel.querySelectorAll('.hm-size-guide__range-tables').forEach((group) => {
      group.hidden = group.dataset.range !== name;
    });
    this.#syncUnit(this.dataset.activeUnit || 'cm');
  }

  /**
   * Show tables matching the active unit. Ranges that only ship a `cm` table
   * keep showing it (unit toggle is a no-op for them).
   */
  #syncUnit(unit) {
    this.dataset.activeUnit = unit;

    this.querySelectorAll('.hm-size-guide__unit').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.unit === unit));
    });

    this.querySelectorAll('.hm-size-guide__range-tables').forEach((group) => {
      const hasUnit = group.querySelector(`.hm-size-guide__table[data-unit="${unit}"]`);
      group.querySelectorAll('.hm-size-guide__table').forEach((table) => {
        // Fall back to cm when the requested unit is unavailable for this range.
        const target = hasUnit ? unit : 'cm';
        table.hidden = table.dataset.unit !== target;
      });
    });
  }
}

if (!customElements.get('hm-size-guide')) {
  customElements.define('hm-size-guide', HmSizeGuide);
}
